import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { renderTitle } from './getSalesAttributionColumns';
import { formatNumWithKMB, formatNumWithKMBAlt } from './helperMethods';
import _ from 'lodash';
import { colorConstants } from 'config/colorConstants';
import UpAltSvg from 'assets/svg/UpAlt';
import DownSvg from 'assets/svg/Down';
import { trackEventMixpanel } from './mixpanel';
import { PRICE_TRACKING_TABLE_ROW_EXPANDED } from './mixpanelEventNames';

export const getPriceTrackingColumns = (
    data: any[],
    weeksSelected: any[],
    expandedIndex: null | number,
    setExpandedIndex: Function,
    rowHeader: string,
    selectedTimePeriodDropdown: string,
    columnLabel: string | undefined,
    rowLabel: string | undefined,
    decimalForNonPercentage: number,
    decimalForPercentage: number,
) => {
    let result: any[] = [];
    let updatedWeeksSelected = [...weeksSelected];
    let columns =
        data && Array.isArray(data) && data.length > 0
            ? ['label', ...Object.keys(data[0]).filter((key) => key !== 'label')].slice(0, -1)
            : [];
    let children: any[] = [];
    result = columns.map((key, idx) => {
        if (idx > 0 && idx !== columns?.length - 1) {
            if (selectedTimePeriodDropdown === 'custom') {
                updatedWeeksSelected = [{ label: 'Custom Period', value: 1 }];
            }
            children = updatedWeeksSelected?.map((item, index: number) => {
                return {
                    title: renderTitle(
                        item?.label,
                        idx === 0 ? null : 'center',
                        undefined,
                        'text-purpleFont !font-bold',
                        graphXAxisLabelFontSize,
                    ),
                    dataIndex: key,
                    width: 190,
                    key: key,
                    onCell: (rowData: any, rowIndex: any) => {
                        const value = rowData[key]?.nested?.[item?.value]?.val;
                        const highlightCondition =
                            (columnLabel ? columnLabel === key : true) &&
                            (rowLabel ? rowLabel === rowData?.label : true) &&
                            !_.isNil(value) &&
                            value === 100;
                        const hideNestedData =
                            rowData['nestedIndex'] !== null && rowData['nestedIndex'] !== expandedIndex;
                        return {
                            style: {
                                textAlign: idx === 0 ? 'auto' : 'center',
                                backgroundColor:
                                    highlightCondition && rowData?.isExpandable //highlighting cells based on value
                                        ? colorConstants.lightGreyAlt
                                        : expandedIndex !== null &&
                                          (rowData['nestedIndex'] === expandedIndex || rowIndex === expandedIndex)
                                        ? colorConstants.lightPurple
                                        : 'white',
                                borderRight:
                                    index < updatedWeeksSelected?.length - 1 ? 'none' : '1px solid rgba(0,0,0,.06)',
                            },
                            colSpan: hideNestedData ? 0 : 1,
                        };
                    },
                    render: (columnData: any, rowData: any, rowIndex: number) => {
                        const hideNestedData =
                            rowData['nestedIndex'] !== null && rowData['nestedIndex'] !== expandedIndex;
                        const cellObj = columnData['nested']?.[item?.value];
                        if (!hideNestedData) {
                            if (_.isNil(cellObj?.val)) return;
                            return (
                                <div className="flex justify-center font-xs items-center">
                                    {cellObj?.pre}
                                    {cellObj?.post === '%'
                                        ? formatNumWithKMBAlt(cellObj?.val, decimalForPercentage)
                                        : formatNumWithKMBAlt(
                                              cellObj.val,
                                              decimalForNonPercentage,
                                              decimalForNonPercentage,
                                          )}
                                    {cellObj?.post}
                                    {rowData?.isExpandable && (
                                        <span
                                            style={{ background: columnData['nested']?.[item?.value]?.color }}
                                            className=" ml-1 h-2 w-2"
                                        ></span>
                                    )}
                                </div>
                            );
                        }
                    },
                };
            });
        } else children = [];

        return {
            title: renderTitle(
                idx === 0 ? rowHeader : idx === columns?.length - 1 ? '' : key,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            children,
            dataIndex: key,
            width: idx === columns?.length - 1 ? 20 : 190,
            key: key,
            fixed: idx === 0 ? 'left' : idx === columns?.length - 1 ? 'right' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (rowData: any, rowIndex: any) => {
                const hideNestedData = rowData['nestedIndex'] !== null && rowData['nestedIndex'] !== expandedIndex;

                return {
                    style: {
                        textAlign: idx === 0 ? 'auto' : 'center',
                        backgroundColor:
                            expandedIndex !== null &&
                            (rowData['nestedIndex'] === expandedIndex || rowIndex === expandedIndex)
                                ? colorConstants.lightPurple
                                : 'white',
                        borderRight: idx === columns?.length - 1 ? 'none' : 'auto',
                    },
                    colSpan: hideNestedData ? 0 : 1,
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                const hideNestedData = rowData['nestedIndex'] !== null && rowData['nestedIndex'] !== expandedIndex;
                if (!hideNestedData && typeof columnData === 'string') {
                    return (
                        <div
                            style={{ marginLeft: rowData['isExpandable'] === false ? 10 : 0 }}
                            className="font-xs text-greyFont font-medium"
                        >
                            {columnData}
                        </div>
                    );
                }
                // if index is last render dropdown
                if (!hideNestedData && idx === columns.length - 1 && rowData['isExpandable']) {
                    return (
                        <span
                            onClick={() => {
                                //resetting
                                if (expandedIndex === rowIndex) setExpandedIndex(null);
                                else {
                                    setExpandedIndex(rowIndex);
                                    //mixpanel event
                                    trackEventMixpanel(PRICE_TRACKING_TABLE_ROW_EXPANDED, {
                                        expandedRowName: rowData?.label,
                                    });
                                }
                            }}
                            className="cursor-pointer flex justify-center items-center"
                        >
                            {expandedIndex === rowIndex ? <UpAltSvg /> : <DownSvg />}
                        </span>
                    );
                }
            },
        };
    });

    return result;
};
